import { format } from 'date-fns';
import { createContext, FC, ReactNode, useContext, useRef } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useClient } from 'urql';
import { createStore, useStore } from 'zustand';
import { patternToEmployeeSchedule } from '../../../helpers/BuildWorkingPattern';
import {
  EmployeeIdWorkingPatternsDocument,
  EmployeeIdWorkingPatternsQuery,
  EmployeeIdWorkingPatternsQueryVariables,
  WorkingPatternPeriods
} from '../../../services/gql/graphql.generated';
import { LoggerLevel, useLogger } from '../../../services/logging/Logger';

export enum TeamMemberUpdateType {
  SHIFTS,
  HEADER,
  ALL
}

export interface TeamMemberService {
  updateNeeded?: TeamMemberUpdateType;
  forceUpdate: (type: TeamMemberUpdateType) => void;
  updateComplete: (type: TeamMemberUpdateType) => void;
  getSkillsData: (employeeId: string) => any;
  getAvailabilitySchedule: (userId: string) => any;
}

type TeamMemberStore = ReturnType<typeof makeStore>;

const makeStore = (
  log: (msg: string, level: LoggerLevel) => void,
  client: ReturnType<typeof useClient>,
  handleError: (error: unknown) => void
) =>
  createStore<TeamMemberService>()((set, get) => ({
    forceUpdate: (type: TeamMemberUpdateType) => {
      set({ updateNeeded: type });
    },
    updateComplete: (type: TeamMemberUpdateType) => {
      if (get().updateNeeded === type) {
        set({ updateNeeded: undefined });
      }
    },
    getSkillsData: async (_employeeId: string) => {
      return {};
    },
    getAvailabilitySchedule: async (userId: string) => {
      if (!userId) {
        log('Expecting user id', LoggerLevel.Error);
        return;
      }

      const response = await client
        .query<EmployeeIdWorkingPatternsQuery, EmployeeIdWorkingPatternsQueryVariables>(
          EmployeeIdWorkingPatternsDocument,
          {
            dateAndPeriods: {
              date: format(new Date(), 'yyyy-MM-dd'),
              periods: 7
            },
            id: userId
          }
        )
        .toPromise();

      if (response.error) {
        handleError(response.error);
      }

      const data = response.data?.workingPatternsByEmployeeId;

      if (!data) {
        log('Expecting availability schedule', LoggerLevel.Error);
        return;
      }

      return patternToEmployeeSchedule(data as WorkingPatternPeriods);
    }
  }));

const TeamMemberContext = createContext<TeamMemberStore | null>(null);

export const TeamMemberProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { log } = useLogger();
  const client = useClient();
  const handleError = useErrorHandler();

  const storeRef = useRef<TeamMemberStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = makeStore(log, client, handleError);
  }

  return <TeamMemberContext.Provider value={storeRef.current}>{children}</TeamMemberContext.Provider>;
};

export const useTeamMemberService = <T,>(selector: (state: TeamMemberService) => T): T => {
  const store = useContext(TeamMemberContext);
  if (!store) {
    throw new Error('useTeamMemberService must be used within a TeamMemberProvider');
  }
  return useStore(store, selector);
};
